import {Component, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {MenuItem, MessageService} from 'primeng/api';
import {
  OfferTextAlternateLeaseTitle,
  OfferTextSaveResponse,
  OfferTextSelectOption,
  OfferTextTaglineType,
  OfferTextVehicleCondition,
  UseDefaultTagline
} from './offer-text.models';
import {
  buildNewAlternateLeaseTitleOptions,
  buildUsedAlternateLeaseTitleOptions,
  offerTextAprPricingTitleOptions,
  offerTextBuyTitleOptions,
  offerTextInventoryButtonTextOptions,
  offerTextLeaseDownTextOptions,
  offerTextLeaseTitleTextOptions,
  offerTextMarketValueTextOptions,
  offerTextMsrpTitleOptions
} from './offer-text-options.constants';
import {OfferTextFormService} from './offer-text-form.service';
import {NavigationService} from '../../../../../service/navigation.service';
import {AdTypesSettingsService} from '../../../../../service/ad-types-settings.service';

const accountIdRadix = 10;
const defaultAccountId = 0;
const failedLoadSettingsDetail = 'Failed to load offer sets settings';
const failedSaveSettingsDetail = 'Failed to save offer sets settings';
const navigationConfigurationFile = 'settings-view-side-menu.configuration.json';
const newVehicleCondition: OfferTextVehicleCondition = 'new';
const offerSetsPageTitle = 'Offer Sets';
const savedSettingsDetail = 'Offer Sets settings updated';
const usedVehicleCondition: OfferTextVehicleCondition = 'used';
const validationErrorDetail = 'Please fix validation errors before saving';

@Component({
  selector: 'auto-offer-text',
  templateUrl: './offer-text.component.html',
  styleUrls: ['./offer-text.component.scss'],
  standalone: false
})
export class OfferTextComponent implements OnInit {
  accountId = defaultAccountId;
  readonly aprPricingTitleOptions = [...offerTextAprPricingTitleOptions];
  readonly buyTitleOptions = [...offerTextBuyTitleOptions];
  readonly inventoryButtonTextOptions = [...offerTextInventoryButtonTextOptions];
  readonly leaseDownTextOptions = [...offerTextLeaseDownTextOptions];
  readonly leaseTitleTextOptions = [...offerTextLeaseTitleTextOptions];
  newAlternateLeaseTitleOptions: Array<OfferTextSelectOption<OfferTextAlternateLeaseTitle>> = [];
  readonly newMsrpTitleOptions = [...offerTextMsrpTitleOptions];
  newVehicleCondition = newVehicleCondition;
  settingsSideNav: MenuItem[] = [];
  taglineTypeOptions: Array<OfferTextSelectOption<OfferTextTaglineType>> = [
    {label: 'Marquee', value: 'marquee'},
    {label: 'Static', value: 'static'}
  ];
  usedAlternateLeaseTitleOptions: Array<OfferTextSelectOption<OfferTextAlternateLeaseTitle>> = [];
  readonly usedMarketValueTextOptions = [...offerTextMarketValueTextOptions];
  usedVehicleCondition = usedVehicleCondition;
  useDefaultTaglineOptions: Array<OfferTextSelectOption<UseDefaultTagline>> = [
    {label: 'Always use Default Tagline', value: '1'},
    {label: 'Use Default Tagline as a fallback', value: '0'}
  ];
  formGroup!: FormGroup;
  isLoadingOfferTextSettings = false;
  isSaving = false;
  showSaveConfirmDialog = false;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private offerTextFormService: OfferTextFormService,
    private adTypesSettingsService: AdTypesSettingsService,
    private messageService: MessageService
  )
  {
    this.formGroup = this.offerTextFormService.formGroup;
  }

  /**
   * The nginit entry point.
   */
  ngOnInit(): void
  {
    this.titleService.setTitle(offerSetsPageTitle);
    this.route.paramMap.subscribe((params) => {
      const accountIdParam = params.get('accountId') ?? defaultAccountId.toString();
      const parsedAccountId = Number.parseInt(accountIdParam, accountIdRadix);
      this.accountId = defaultAccountId;

      if (!Number.isNaN(parsedAccountId)) {
        this.accountId = parsedAccountId;
      }

      this.initNavigation();
      this.loadOfferTextSettings();
    });
  }

  /**
   * Initialize the side navigation menu.
   */
  initNavigation(): void
  {
    this.navigationService.getMenuItems(this.accountId, navigationConfigurationFile)
      .then((navItems) => {
        this.settingsSideNav = navItems;
      });
  }

  /**
   * Checks whether the Save button should be disabled.
   */
  getSaveButtonDisabled(): boolean
  {
    let isDisabled = true;

    const canSave = this.offerTextFormService.isDirty() &&
      this.offerTextFormService.isValid() &&
      this.isValidAccountId() &&
      !this.isLoadingOfferTextSettings;

    if (canSave) {
      isDisabled = false;
    }

    return isDisabled;
  }

  /**
   * Shows the save confirmation dialog or displays validation errors.
   */
  saveSettings(): void
  {
    if (!this.offerTextFormService.isValid()) {
      this.offerTextFormService.markAllAsTouched();
      this.showSaveValidationError();
    } else if (this.canShowSaveConfirmDialog()) {
      this.showSaveConfirmDialog = true;
    }
  }

  /**
   * Called when the user confirms they want to save changes.
   */
  onSaveConfirmed(): void
  {
    this.persistOfferTextSettings(true);
  }

  /**
   * Called when the user confirms they want to save and sync changes.
   */
  onSaveAndSyncConfirmed(): void
  {
    this.persistOfferTextSettings(false);
  }

  /**
   * Called when the user cancels the save confirmation dialog.
   */
  onSaveConfirmCancelled(): void
  {
    this.showSaveConfirmDialog = false;
  }

  /**
   * Selects the new condition form group used by the visible text panel.
   */
  get newConditionFormGroup(): FormGroup
  {
    return this.formGroup.controls[newVehicleCondition] as FormGroup;
  }

  /**
   * Selects the used condition form group used by the visible text panel.
   */
  get usedConditionFormGroup(): FormGroup
  {
    return this.formGroup.controls[usedVehicleCondition] as FormGroup;
  }

  /**
   * Loads offer set visible text settings for this account.
   */
  private loadOfferTextSettings(): void
  {
    if (this.isValidAccountId()) {
      this.isLoadingOfferTextSettings = true;
      this.adTypesSettingsService.getOfferTextSettings(this.accountId).subscribe({
        next: (settingsPayload) => {
          this.setAlternateLeaseTitleOptions(settingsPayload.vehicleAdsEnabled);
          this.offerTextFormService.hydrate(settingsPayload);
        },
        error: () => {
          this.handleLoadError();
        },
        complete: () => {
          this.isLoadingOfferTextSettings = false;
        }
      });
    }
  }

  private setAlternateLeaseTitleOptions(vehicleAdsEnabled: boolean): void
  {
    this.newAlternateLeaseTitleOptions = buildNewAlternateLeaseTitleOptions(vehicleAdsEnabled);
    this.usedAlternateLeaseTitleOptions = buildUsedAlternateLeaseTitleOptions(vehicleAdsEnabled);
  }

  /**
   * Persists visible text settings using the existing Offer Sets endpoint.
   */
  private persistOfferTextSettings(skipOfferTextSync: boolean): void
  {
    if (this.isSaving) {
      return;
    }

    this.showSaveConfirmDialog = false;
    this.isSaving = true;
    const payload = this.offerTextFormService.buildPayload(this.accountId, skipOfferTextSync);

    this.adTypesSettingsService.saveOfferTextSettings(payload).subscribe({
      next: (response) => {
        this.handleSaveResponse(response, skipOfferTextSync);
      },
      error: () => {
        this.handleSaveError();
      },
      complete: () => {
        this.isSaving = false;
      }
    });
  }

  /**
   * Determines whether the save confirmation dialog can be displayed.
   */
  private canShowSaveConfirmDialog(): boolean
  {
    const canShow = !this.isLoadingOfferTextSettings &&
      !this.isSaving &&
      this.offerTextFormService.isDirty() &&
      this.offerTextFormService.isValid() &&
      this.isValidAccountId();

    return canShow;
  }

  /**
   * Checks whether the route account ID can be used by account settings calls.
   */
  private isValidAccountId(): boolean
  {
    const isValidAccountId = Number.isInteger(this.accountId) && this.accountId > defaultAccountId;

    return isValidAccountId;
  }

  /**
   * Handles failed initial settings load.
   */
  private handleLoadError(): void
  {
    this.isLoadingOfferTextSettings = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: failedLoadSettingsDetail
    });
  }

  /**
   * Handles the response from a settings save request.
   */
  private handleSaveResponse(response: OfferTextSaveResponse, skipOfferTextSync: boolean): void
  {
    if (response.settingsSaved) {
      this.offerTextFormService.markAsPristine();
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: savedSettingsDetail
      });

      if (!skipOfferTextSync) {
        this.handleSyncResponse(response);
      }

      return;
    }

    this.handleSaveError();
  }

  /**
   * Provides feedback for the synchronization portion of a save request.
   */
  private handleSyncResponse(response: OfferTextSaveResponse): void
  {
    let severity = 'warn';
    let summary = 'Warning';

    if (response.success) {
      severity = 'info';
      summary = 'Sync Status';
    }

    this.messageService.add({
      severity,
      summary,
      detail: response.syncMessage
    });
  }

  /**
   * Handles failed saves.
   */
  private handleSaveError(): void
  {
    this.isSaving = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: failedSaveSettingsDetail
    });
  }

  /**
   * Shows validation feedback when save is requested with invalid data.
   */
  private showSaveValidationError(): void
  {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: validationErrorDetail
    });
  }
}
